/**
 * Basic Usage:
 *  useWebviewEmit('some_event');
 *
 * Show a native alert (title + message):
 *  useWebviewEmit('info_alert_tap', { title: 'Title', message: 'Message' });
 *
 * `info_alert_tap` is the app's global alert presenter (handled in
 * BaseWebViewController -> showInfoAlert), and it reads `title`/`message` from
 * the top level of the data object.
 *
 * IMPORTANT: the event name must exist in the native `CommonEvent.Event` enum.
 * The host decodes the whole message envelope against that enum, so an unknown
 * name fails to decode and the app raises a user-visible
 * "Unparseable Message [...]" alert — it is not silently ignored. Add a case to
 * the iOS and Android enums, then add the name to `NATIVE_WEBVIEW_EVENTS`
 * below, before emitting a new event.
 */

/**
 * Event names known to have a matching case in the native `CommonEvent.Event`
 * enum, i.e. names the app can actually decode and handle.
 *
 * Keep sorted. Names that arrive at runtime — CMS marketing-block
 * `clickEvent`s, the `?event=` query param, dev-tools entries — are not
 * covered here, which is why an unregistered name only warns.
 *
 * @type {readonly string[]}
 */
export const NATIVE_WEBVIEW_EVENTS = Object.freeze([
    "close_webview",
    "clubhouse_tap",
    "course_search_tap",
    "dim_status_bar",
    "enter_full_screen",
    "exit_full_screen",
    "golf_bag_tap",
    "hide_bottom_nav",
    "hide_top_nav",
    "home_advanced_stats_tap",
    "home_basic_stats_tap",
    "home_last_round_tap",
    "home_play_tap",
    "home_practice_priority_tap",
    "home_upgrade_tap",
    "home_video_tap",
    "info_alert_tap",
    "join_round",
    "open_link",
    "open_subscription_manager",
    "play_cta_tap",
    "purchase_complete",
    "purchase_iap",
    "set_bg_color",
    "share",
    "share_yir",
    "show_bottom_nav",
    "show_top_nav",
    "stats_practice_priority_tap",
    "stats_round_edit_menu_tap",
    "undim_status_bar",
    "upgrade_tap",
    "webview_ready",
]);

/**
 * @param {string} event
 * @returns {boolean} Whether the native apps are known to handle the event.
 */
export function isNativeWebviewEvent(event) {
    return NATIVE_WEBVIEW_EVENTS.includes(event);
}

let deviceType = "ios";

export function useSetDeviceType(type = "ios") {
    deviceType = type;
}

/** @type {Set<(event: string, data: object) => void>} */
let emitListeners = new Set();

/**
 * Watch everything this module sends the host, for state the host keeps and the web side
 * has to stay in step with. `host-chrome.js` tracks the nav bars and background this way,
 * so an emit that bypasses a claim cannot leave its record of the host stale.
 *
 * @param  {(event: string, data: object) => void} listener
 * @return {() => void} Unsubscribes.
 */
export function onWebviewEmit(listener) {
    emitListeners.add(listener);

    return function stop() {
        emitListeners.delete(listener);
    };
}

// by convention, composable function names start with "use"
export function useWebviewEmit(event, data = {}, status = "success") {
    // Before the send, not after: the send below swallows its own failures, so there is
    // no "it worked" to wait for.
    [...emitListeners].forEach((listener) => listener(event, data));

    if (!isNativeWebviewEvent(event)) {
        console.warn(
            `Webview event "${event}" is not a known native event. If the app has no case for it in CommonEvent.Event, the host rejects the message with an "Unparseable Message" alert.`
        );
    }

    // String event
    let eventData = {
        event: event,
        data: data,
        status: status,
    };

    // Non-JSON event
    if (!isJsonEvent()) {
        // Android can only receive primitive data types
        eventData = JSON.stringify(eventData);
    }

    try {
        if (isAndroid()) {
            logEmit("android", event, eventData);
            window.Android.postMessage(eventData);
            return self;
        }
        if (isIos()) {
            logEmit("ios", event, eventData);
            window.webkit.messageHandlers.callbackHandler.postMessage(
                eventData
            );
            return self;
        }
        console.warn("Device type was not detected");
    } catch (e) {
        console.warn("Communication with the host app failed");
    }
}

function logEmit(deviceType, event, eventData) {
    // Can leave console.log() in, app users can't see the output
    console.log([event, eventData]);

    /**
     * Create hidden element with event information for Dusk
     * tests to verify events
     */
    let container = document.createElement("div");
    container.setAttribute("style", "display:none");
    container.id = "device-emit-log";
    container.setAttribute("data-event", event);
    container.setAttribute("data-device-type", deviceType);
    container.setAttribute("data-event-data-raw", eventData);
    container.setAttribute("data-event-data", JSON.stringify(eventData));

    // enable to see output on screen
    /*
    container.innerHTML = `
        <div class="device-type">${deviceType}</div>
        <div class="event">${event}</div>
        <div class="event-data-raw">${eventData}</div>
        <div class="event-data">${JSON.stringify(eventData)}</div>
    `;
    */
    if (document.querySelector("#device-emit-log")) {
        document.querySelector("#device-emit-log").replaceWith(container);
    } else {
        document.querySelector("body").append(container);
    }
}

/**
 * @returns {string} The current device type ("ios" | "android").
 */
export function useDeviceType() {
    return deviceType;
}

/**
 * @returns {boolean}
 */
export function isAndroid() {
    return deviceType === "android";
}

/**
 * @returns {boolean}
 */
export function isIos() {
    return deviceType === "ios";
}

/**
 *
 * @returns {*|define.amd.jQuery}
 */
function isJsonEvent() {
    return isIos();
}
